import React, { useMemo, type ReactNode } from 'react';
import { ForgeScreenEvent } from '@atlassian/jira-forge-ui-analytics/src/common/ui/index.tsx';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { EditAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/custom-field/index.tsx';
import type { ForgeUiAnalyticsAttributes } from '@atlassian/jira-forge-ui-types/src/common/types/analytics.tsx';
import type { ForgeCustomFieldValue } from '@atlassian/jira-forge-ui-types/src/common/types/contexts/custom-field.tsx';
import type { CustomFieldExtension } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import {
	toIssueTypeId,
	toProjectId,
	toProjectKey,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	isBuiltInJiraFieldRender,
	getExtensionIdFromLegacyFieldType,
	isForgeLegacyFieldType,
} from '../utils/index.tsx';

import { EditMode } from './edit/index.tsx';
import { FieldContainer } from './FieldContainer.tsx';
import { ForgeFieldMessage } from './forge-field-message/index.tsx';
import {
	type FieldProps,
	type IssueOrRequestType,
	type SelectedProject,
	REQUEST_TYPE,
} from './types.tsx';
import { transformValueToExtensionData } from './utils/index.tsx';

export type Props = {
	fieldType: string;
	fieldId: string;
	fieldName: string;
	fieldProps: FieldProps<ForgeCustomFieldValue>;
	error?: string;
	selectedProject: SelectedProject | undefined;
	selectedIssueType: IssueOrRequestType | undefined;
	extension: CustomFieldExtension | undefined | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderDefaultComponent: (fieldProps: FieldProps<any>, error?: string) => ReactNode;
};

export type ForgeFieldProps = Props & {
	renderFieldMessage: (error?: string) => ReactNode;
};

export const ForgeFieldView = ({
	fieldType,
	fieldName,
	fieldId,
	fieldProps,
	error,
	selectedProject,
	selectedIssueType,
	extension,
	renderDefaultComponent,
}: Props): React.JSX.Element | null => {
	const { onChange, value } = fieldProps;

	const extensionPayload = useMemo(() => {
		if (value === undefined) {
			return {
				fieldValue: null,
			};
		}
		return { fieldValue: transformValueToExtensionData(value, extension) };
	}, [extension, value]);

	const extensionData = useMemo(() => {
		const defaultExtensionData = {
			fieldId,
			fieldType: isForgeLegacyFieldType(fieldType)
				? (getExtensionIdFromLegacyFieldType(fieldType) ?? fieldType)
				: fieldType,
			fieldName,
			renderContext: 'issue-create',
			...(fg('ditto-fcf-support-new-manifest-on-frontend') && {
				experience: 'issue-create' as const,
			}),
		};

		if (!selectedProject || !selectedIssueType) {
			return defaultExtensionData;
		}

		const issueTypeId = toIssueTypeId(
			selectedIssueType.type === REQUEST_TYPE
				? selectedIssueType.issueTypeId
				: selectedIssueType.value,
		);

		return {
			...defaultExtensionData,
			project: {
				id: toProjectId(selectedProject.value),
				key: toProjectKey(selectedProject.projectKey),
				type: selectedProject.projectType,
			},
			issue: {
				type:
					(selectedIssueType.type === REQUEST_TYPE && selectedIssueType.issueTypeName) ||
					selectedIssueType.label,
				typeId: issueTypeId,
			},
		};
	}, [selectedProject, selectedIssueType, fieldId, fieldName, fieldType]);

	const analyticsAttributes = useMemo(
		() => ({
			renderContext: 'issue-create',
			fieldType: extension?.properties.type,
			module: extension?.type,
			...(extension ? getAnalyticsAttributesFromExtension(extension) : {}),
		}),
		[extension],
	);

	if (!extension) {
		return null;
	}

	const renderDefaultView = () => (
		<>
			{renderDefaultComponent(fieldProps, error)}
			<ForgeScreenEvent
				attributes={{
					...analyticsAttributes,
					renderMode: 'default',
				}}
			/>
		</>
	);

	if (fg('ditto-fcf-support-new-manifest-on-frontend')) {
		if (isBuiltInJiraFieldRender(extension, 'edit', 'issue-create')) {
			return renderDefaultView();
		}
	} else if (!extension.properties.edit) {
		return renderDefaultView();
	}

	return (
		<EditMode
			extension={extension}
			extensionData={extensionData}
			extensionPayload={extensionPayload}
			analyticsAttributes={analyticsAttributes}
			onChange={onChange}
		/>
	);
};

export const ForgeField = (props: ForgeFieldProps): React.JSX.Element => {
	const { renderFieldMessage, ...rest } = props;
	const { fieldId, extension, error } = rest;
	const analyticsAttributes = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				renderContext: 'issue-create',

				...(extension && getAnalyticsAttributesFromExtension(extension)),
			}) as ForgeUiAnalyticsAttributes,
		[extension],
	);

	return (
		<EditAnalyticsWrapper attributes={analyticsAttributes}>
			<FieldContainer width="100%">
				<ForgeFieldView {...rest} />
				<ForgeFieldMessage
					error={error}
					fieldId={fieldId}
					renderFieldMessage={renderFieldMessage}
				/>
			</FieldContainer>
		</EditAnalyticsWrapper>
	);
};
